import React, {FC, useState} from "react";
import LayoutPage from "components/LayoutPage/LayoutPage";
import BgGlassmorphism from "../../components/BgGlassmorphism/BgGlassmorphism";
import facebookSvg from "images/Facebook.svg";
import googleSvg from "images/Google.svg";
import githubSvg from "images/github.svg";
import hidepwd from "../../images/hide-password.svg";
import showpwd from "../../images/show-password.svg";
import Input from "components/Input/Input";
import ButtonPrimary from "components/Button/ButtonPrimary";
import NcLink from "components/NcLink/NcLink";
import { Helmet } from "react-helmet";
import {
  AuthProvider, browserSessionPersistence,
  createUserWithEmailAndPassword,
  fetchSignInMethodsForEmail,
  setPersistence,
  signInWithEmailAndPassword,
  signInWithPopup,
  updateProfile,
  sendEmailVerification
} from "firebase/auth";
import {auth, db, facebookProvider, githubProvider, googleProvider} from "../../configs/firebase-config";
import {collection, doc, getDocs, query, setDoc, where } from "firebase/firestore";
import { useHistory } from "react-router";
import {authChecks, displayToast, toastType} from "../../global/Functions";

export interface PageSignUpProps {
  className?: string;
}

const providers: any = {
  google: googleProvider,
  facebook: facebookProvider,
  gitHub: githubProvider,
};

const loginSocials = [
  {
    name: "Continue with Facebook",
    href: "#",
    icon: facebookSvg,
    provider: providers.facebook,
  },
  {
    name: "Continue with Github",
    href: "#",
    icon: githubSvg,
    provider: providers.gitHub,
  },
  {
    name: "Continue with Google",
    href: "#",
    icon: googleSvg,
    provider: providers.google,
  },
];

const PageSignUp: FC<PageSignUpProps> = ({ className = "" }) => {
  const [registerEmail, setRegisterEmail] = useState("");
  const [registerFirstName, setRegisterFirstName] = useState("");
  const [registerLastName, setRegisterLastName] = useState("");
  const [registerUsername, setRegisterUsername] = useState("");
  const [registerPassword, setRegisterPassword] = useState("");
  const [registerPasswordConfirm, setRegisterPasswordConfirm] = useState("");
  const [passwordShown, setPasswordShown] = useState(false);
  const [passwordConfirmShown, setPasswordConfirmShown] = useState(false);
  const timeout: number = 5000;
  const signUpDefaultURL = "https://firebasestorage.googleapis.com/v0/b/cryptoverse-921ab.appspot.com/o/profile%2Fdefault%2F676-6764065_default-profile-picture-transparent-hd-png-download.png?alt=media&token=d6a70d9d-0174-4c2c-82e5-0260467c29c3"


  /*TODO: IMPLEMENT PROMPT FOR PASSWORD INPUT*/
  function promptUserForPassword() {
    return window.prompt("Enter password: ");
  }

  const handleSignIn = async (provider: AuthProvider) => {
    setPersistence(auth, browserSessionPersistence).then(async () => {
      signInWithPopup(auth, provider).then(async (userCredentials) => {
        displayToast("Logged In", toastType.success);
        const exists = await authChecks(userCredentials);
        exists ? history.push("/subscription") : history.push("/dashboard")
      }).catch((error) => {
        if (error.code === "auth/account-exists-with-different-credential") {
          let email = error.customData.email;
          fetchSignInMethodsForEmail(auth, email).then((methods) => {
            if (methods[0] === "password") {
              const password = promptUserForPassword(); // TODO: implement promptUserForPassword.
              signInWithEmailAndPassword(auth, email, password!).then(async (userCredentials) => {
                displayToast("Logged In", toastType.success);
                const exists = await authChecks(userCredentials.user);
                exists ? history.push("/subscription") : history.push("/dashboard")
              });
              return;
            }
            displayToast("App registered with " + String([methods[0].split(".")[0]]), toastType.error);
          }).catch((error) => {
            displayToast(error.code, toastType.error);
          });
        }
      });
    })
  }

  const usersCollectionRef = collection(db, "users");
  let history = useHistory();

  const signUp = async (e: any) => {
    e.preventDefault();
    if (registerPassword === registerPasswordConfirm) {
      setPersistence(auth, browserSessionPersistence).then(async () => {
        createUserWithEmailAndPassword(auth, registerEmail, registerPasswordConfirm).then(async (result) => {
          updateProfile(result.user, {displayName: registerUsername,
            photoURL: signUpDefaultURL}).then(async () => {
            await setDoc(doc(db, "users", `${result.user.uid}`), {
              id: result.user.uid,
              firstName: registerFirstName,
              lastName: registerLastName,
              displayName: registerUsername,
              email: registerEmail,
              photoURL: signUpDefaultURL
            });
          })
          await sendEmailVerification(result.user).then(() => {
            history.replace("/verification")
          }).catch((error) => {
            alert(error)
          });
        }).catch((error) => {
          //Implemented Error checks
          if (error.code === "auth/weak-password") {
            displayToast("Please enter a stronger password.", toastType.error);
          } else if (error.code == "auth/email-already-in-use") {
            displayToast("Email already in use.", toastType.error);
          }
        });
      })
    } else {
      displayToast("Passwords don't match", toastType.error);
    }
  };
  return (
    <div className={`PageSignUp ${className}`} data-id="PageSignUp">
      <Helmet>
        <title>Sign up | CryptoVerse</title>
      </Helmet>
      <BgGlassmorphism />
      <LayoutPage
        subHeading="Take a trip to The CryptoVerse today"
        headingEmoji=""
        heading="Create Your Account"
      >
        <div className="max-w-md mx-auto space-y-6">
          <div className="grid gap-3">
            {loginSocials.map((item, index) => (
              <a
                key={index}
                href={item.href}
                onClick={() => handleSignIn(item.provider)}
                className="will-change-transform flex w-full rounded-lg bg-primary-50 dark:bg-neutral-800 px-4 py-3 transform transition-transform sm:px-6 hover:translate-y-[-2px]"
              >
                <img
                  className="flex-shrink-0"
                  src={item.icon}
                  alt={item.name}
                />
                <h3 className="flex-grow text-center text-sm font-medium text-neutral-700 dark:text-neutral-300 sm:text-sm">
                  {item.name}
                </h3>
              </a>
            ))}
          </div>
          {/* OR */}
          <div className="relative text-center">
            <span className="relative z-10 inline-block px-4 font-medium text-sm bg-white dark:text-neutral-400 dark:bg-neutral-900">
              OR
            </span>
            <div className="absolute left-0 w-full top-1/2 transform -translate-y-1/2 border border-neutral-100 dark:border-neutral-800"></div>
          </div>
          {/* FORM */}
          <form
            className="grid grid-cols-1 gap-6"
            action="#"
            method="post"
            onSubmit={signUp}
          >
            <label className="block">
              <span className="text-neutral-800 dark:text-neutral-200">
                First Name
              </span>
              <Input
                type="text"
                placeholder="First Name"
                className="mt-1"
                required
                onChange={(event) => {
                  setRegisterFirstName(event.target.value);
                }}
              />
            </label>
            <label className="block">
              <span className="text-neutral-800 dark:text-neutral-200">
                Last Name
              </span>
              <Input
                type="text"
                placeholder="Last Name"
                className="mt-1"
                required
                onChange={(event) => {
                  setRegisterLastName(event.target.value);
                }}
              />
            </label>
            <label className="block">
              <span className="text-neutral-800 dark:text-neutral-200">
                User Name
              </span>
              <Input
                type="text"
                placeholder="UserName"
                className="mt-1"
                required
                onBlur={async (event) => {
                  const match = await getDocs(query(usersCollectionRef, where("displayName", "==", event.target.value)));
                  if (match.size >= 1) {
                    displayToast("Username Taken", toastType.warn);
                    event.target.value = "";
                  } else {
                    setRegisterUsername(event.target.value);
                  }
                }}
              />
            </label>
            <label className="block">
              <span className="text-neutral-800 dark:text-neutral-200">
                Email address
              </span>
              <Input
                type="email"
                placeholder="name@example.com"
                className="mt-1"
                required
                onChange={(event) => {
                  setRegisterEmail(event.target.value);
                }}
              />
            </label>
            <div className={"pwd-container"} style={{ position: "relative" }}>
              <label className="block">
                <span className="flex justify-between items-center text-neutral-800 dark:text-neutral-200">
                  Password
                </span>
                <Input
                  type={passwordShown ? "text" : "password"}
                  placeholder={
                    passwordShown ? "Shhh! It's a secret..." : "**********"
                  }
                  className="mt-1"
                  required
                  onChange={(event) => {
                    setRegisterPassword(event.target.value);
                  }}
                />
              </label>
              <img
                title={passwordShown ? "Hide password" : "Show password"}
                src={passwordShown ? hidepwd : showpwd}
                onClick={() => {
                  setPasswordShown(!passwordShown);
                }}
                style={{
                  cursor: "pointer",
                  position: "absolute",
                  width: "1.5rem",
                  right: "1.25rem",
                  top: "2.5rem",
                  fill: "white",
                }}
                alt="Show/Hide Password"
              />
            </div>
            <div className={"pwd-container"} style={{ position: "relative" }}>
              <label className="block">
                <span className="flex justify-between items-center text-neutral-800 dark:text-neutral-200">
                  Confirm Password
                </span>
                  <Input
                    type={passwordConfirmShown ? "text" : "password"}
                    placeholder={passwordConfirmShown ? "Confirm Password" : "**********"}
                    className="mt-1"
                    required
                    onChange={(event) => {
                      setRegisterPasswordConfirm(event.target.value);
                    }}
                  />
              </label>
              <img
                title={passwordConfirmShown ? "Hide password" : "Show password"}
                src={passwordConfirmShown ? hidepwd : showpwd}
                onClick={() => {
                  setPasswordConfirmShown(!passwordConfirmShown)
                }}
                style={{
                  cursor: "pointer",
                  position: "absolute",
                  width: "1.5rem",
                  right: "1.25rem",
                  top: "2.5rem",
                  fill: "white",
                }}
                alt="Show/Hide Password"
              />
            </div>
            <ButtonPrimary type="submit">Continue</ButtonPrimary>
          </form>
          <span className="block text-center text-neutral-700 dark:text-neutral-300">
            Already have an account? {` `}
            <NcLink to="/login">Log in</NcLink>
          </span>
        </div>
      </LayoutPage>
    </div>
  );
};

export default PageSignUp;
